import React from 'react';
import { DroppableProvided, Droppable } from 'react-beautiful-dnd';
import { AnyEmptyQuestion, Section } from '../../../../types';
import DraggableQuestionContainer from './DraggableQuestionContainer';
import { QuestionsListContainer } from '../styles';
import AddQuestionButton from '../components/AddQuestionButton';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsEditingAnyQuestion } from '../../../../../../redux/questionnaire/selectors';
import { addEmptyQuestionToSection } from '../../../../../../redux/questionnaire/actions';
import styled from 'styled-components';
import { Button } from '@material-ui/core';

interface DroppableQuestionsListProps {
  questions: AnyEmptyQuestion[];
  sections: Section[];
  droppableProvided: DroppableProvided | null;
  sectionId?: string;
}

const SectionContainer = styled.div`
  margin-bottom: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background-color: #f5f5f5;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 16px;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionQuestions = styled.div`
  background-color: white;
`;

function DroppableQuestionsList({ questions, sections, droppableProvided, sectionId }: DroppableQuestionsListProps) {
  const isEditingAnyQuestion = useSelector(selectIsEditingAnyQuestion);
  const dispatch = useDispatch();

  const handleAddQuestionToSection = (sectionNumber: number) => {
    dispatch(addEmptyQuestionToSection(sectionNumber));
  };

  if (sectionId && sections.length === 1) {
    const section = sections[0];
    return (
      <Droppable droppableId={sectionId}>
        {provided => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {section.questions.map((question, i) => {
              const globalIndex = questions.findIndex(q => q._id === question._id);
              return <DraggableQuestionContainer key={question._id} question={question} index={globalIndex} />;
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  return (
    <QuestionsListContainer ref={droppableProvided?.innerRef} {...droppableProvided?.droppableProps}>
      {sections.map(section => (
        <SectionContainer key={section.section}>
          <SectionHeader>
            <div>
              <div style={{ fontWeight: 600, fontSize: '16px' }}>
                {sections.length === 1 ? 'Questions' : `Question ${section.section}`}
                {section.title && ` - ${section.title}`}
              </div>
              {sections.length > 1 && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Each question is displayed per page
                </div>
              )}
            </div>
            {!isEditingAnyQuestion && (
              <Button
                size="small"
                style={{ backgroundColor: '#4DB181', color: 'white' }}
                variant="contained"
                onClick={() => handleAddQuestionToSection(section.section)}
              >
                + Add Question
              </Button>
            )}
          </SectionHeader>
          <SectionQuestions>
            <Droppable droppableId={`section-${section.section}-questions`}>
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {section.questions.map((question, i) => {
                    const globalIndex = questions.findIndex(q => q._id === question._id);
                    return <DraggableQuestionContainer key={question._id} question={question} index={globalIndex} />;
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </SectionQuestions>
        </SectionContainer>
      ))}
      {droppableProvided?.placeholder}
      {!isEditingAnyQuestion && <AddQuestionButton />}
    </QuestionsListContainer>
  );
}

export default DroppableQuestionsList;
